import qrcode from "qrcode-generator";

/**
 * "Stickers afdrukken" — een vel QR-codes om bij de hokken op te hangen.
 *
 * <p>De dierverzorgers-app scant die code en staat meteen in het juiste hok, klaar om af te tekenen
 * (§22 van het functioneel ontwerp). Dat scheelt in een druk asiel bij elke handeling het zoeken in
 * een lijst van tweehonderd hokken.
 *
 * <p><b>De code draagt het kennel-id en niet het hoknummer.</b> Hoknummers zijn niet uniek over
 * kamers heen — kamer 1 en kamer 2 kunnen allebei een hok 3 hebben — en ze veranderen als een asiel
 * hernummert. Het id doet geen van beide. Het nummer staat er groot bíj, want daar herkent een mens
 * de deur aan; de code is voor de telefoon.
 *
 * <p>Client-side, net als {@code printVerklaring}: wat je op het scherm ziet is wat er uit de printer
 * komt, en er hoeft geen sjabloon op de server bij te blijven.
 */

/** Eén hok op het vel. */
export interface HokSticker {
  kennelAsielId: number;
  kennelNummer: string | null;
  kamerNaam: string;
}

export interface HokstickersPrintOptions {
  /** Kop boven het vel; meestal de naam van de opvang. */
  organisatie: string;
  stickers: HokSticker[];
  /** Datum van afdrukken, al opgemaakt — zodat een oud vel herkenbaar is. */
  datum: string;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Wat de app terugleest. Moet gelijk blijven aan `hokcodeVoor()` in de dierverzorgers-app;
 * verandert deze vorm, dan zijn alle geplakte stickers in één keer onleesbaar.
 */
export function hokcodeVoor(kennelAsielId: number): string {
  return `fawkes://hok/${kennelAsielId}`;
}

function qrSvg(payload: string): string {
  const qr = qrcode(0, "M");
  // De generator leest één byte per teken; de payload eerst naar UTF-8-bytes brengen. Bij deze code
  // zitten er geen accenten in, maar dezelfde regel als bij de koppelcode voorkomt dat dat ooit
  // stilletjes fout gaat.
  qr.addData(String.fromCharCode(...new TextEncoder().encode(payload)), "Byte");
  qr.make();
  return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
}

export function printHokstickers(o: HokstickersPrintOptions): void {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) return;

  const kaartjes = o.stickers
    .map(
      (s) => `<div class="sticker">
        <div class="qr">${qrSvg(hokcodeVoor(s.kennelAsielId))}</div>
        <div class="tekst">
          <div class="nummer">${esc(s.kennelNummer ? `Hok ${s.kennelNummer}` : `Hok ${s.kennelAsielId}`)}</div>
          <div class="kamer">${esc(s.kamerNaam)}</div>
        </div>
      </div>`,
    )
    .join("");

  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(
    `Hokstickers — ${o.organisatie}`,
  )}</title>
<style>
  body { font: 13px/1.4 -apple-system, Segoe UI, Roboto, sans-serif; color: #111827; margin: 16mm; }
  h1 { font-size: 15px; margin: 0 0 2px; }
  .sub { color: #6b7280; font-size: 12px; margin: 0 0 14px; }
  .vel { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6mm; }
  /* Vaste hoogte: op een vel dat je in stroken knipt moeten de kaartjes even groot zijn, ook als de
     ene kamernaam langer is dan de andere. */
  .sticker {
    display: flex; align-items: center; gap: 4mm;
    border: 1px dashed #c3c8cf; border-radius: 3mm;
    padding: 4mm; height: 26mm; box-sizing: border-box;
    break-inside: avoid; page-break-inside: avoid;
  }
  .qr { width: 18mm; height: 18mm; flex: none; }
  .qr svg { width: 100%; height: 100%; display: block; }
  .tekst { min-width: 0; }
  .nummer { font-size: 15px; font-weight: 700; line-height: 1.2; }
  .kamer { font-size: 11px; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  @media print { body { margin: 10mm; } .sub { color: #6b7280; } }
</style></head><body>
  <h1>${esc(o.organisatie)}</h1>
  <p class="sub">Hokstickers · ${esc(o.datum)} · ${o.stickers.length} ${
    o.stickers.length === 1 ? "hok" : "hokken"
  }</p>
  <div class="vel">${kaartjes}</div>
</body></html>`);
  win.document.close();
  // Wachten tot het venster zijn eigen inhoud heeft opgemaakt; direct printen levert op sommige
  // browsers een leeg vel op.
  win.focus();
  win.setTimeout(() => win.print(), 250);
}
